/* CUSTOM EDITS (search CUSTOM: to find all, or by tag):
 * [CUSTOM:overflow-list-reflow]     — force overflow-list reflow after variant update
 * [CUSTOM:variant-url-sync]         — keep product card link URL in sync with selected variant
 * [CUSTOM:swatch-legend-update]     — update legend text when a swatch is selected
 * [CUSTOM:unavailable-swatch]       — handle unavailable swatches by finding first available variant
 * [CUSTOM:button-price-update]      — morph the add-to-cart button price on variant change
 * [CUSTOM:button-text-update]       — morph the add-to-cart button label on variant change
 * [CUSTOM:compare-price-attr-sync]  — sync data-has-compare-at-price on the wrapper after variant change
 * [CUSTOM:swatch-variant-media]     — swap the card-gallery image by toggling hidden
 *                                     [data-card-media-id] layers (replaces slideshow.select)
 * [CUSTOM:hover-variant-metafield]  — re-render the hover (secondary) image from the
 *                                     variant-update section HTML on variant change
 */

import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // [CUSTOM:swatch-variant-media] Warm the hidden variant images once the user hovers
    // the card (intent), so the swatch swap has no first-hover flash. Nothing downloads
    // until this fires; see #warmVariantMedia.
    this.addEventListener('pointerenter', this.#warmVariantMedia, { once: true });

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#updateSecondaryImage();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  // [CUSTOM:overflow-list-reflow]
  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    // [CUSTOM:button-price-update] The card renders the quick-add twice — desktop overlay
    // (_product-card-gallery) and mobile (_product-card-quick-add) — so there are multiple
    // button prices. Snapshot the source before the card morph below can mutate it, then
    // morph every button price (querySelector previously only updated the first/desktop one,
    // leaving the mobile price stale).
    const buttonPriceContainers = this.querySelectorAll(`.add-to-cart-text__price [ref="priceContainer"]`);
    const newPriceSnapshot = newPriceElement?.cloneNode(true);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }

    if (newPriceSnapshot) {
      buttonPriceContainers.forEach((buttonPriceContainer) => {
        morph(buttonPriceContainer, /** @type {Element} */ (newPriceSnapshot.cloneNode(true)));
      });
    }

    // [CUSTOM:button-text-update]
    const buttonLabel = this.querySelector('.quick-add__button--add .add-to-cart-text__content > span:first-child');
    const newButtonLabel = event.detail.data.html.querySelector('.quick-add__button--add .add-to-cart-text__content > span:first-child');
    if (buttonLabel && newButtonLabel) {
      morph(buttonLabel, /** @type {Element} */ (newButtonLabel.cloneNode(true)));
    }

    // [CUSTOM:compare-price-attr-sync] data-has-compare-at-price is baked at Liquid render
    // time, so it goes stale when a variant is picked client-side. The re-rendered price HTML
    // (used for the morphs above) contains a `.compare-at-price` element only when the newly
    // selected variant is on sale, so derive the flag from that and mirror it onto every
    // wrapper (desktop overlay + mobile) to keep the compare-at container-query layout correct.
    if (newPriceElement) {
      const hasCompareAtPrice = newPriceElement.querySelector('.compare-at-price') ? 'true' : 'false';
      this.querySelectorAll('.custom-quick-add-wrapper').forEach((wrapper) => {
        wrapper.setAttribute('data-has-compare-at-price', hasCompareAtPrice);
      });
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Show the card-gallery image for the selected variant.
   */
  #updateVariantImages() {
    // [CUSTOM:swatch-variant-media] swap the visible layer instead of driving a slideshow.
    // Fall back to the default (primary) layer when the selected variant has no image, or
    // its media isn't rendered as a layer — otherwise the card would keep showing the
    // previously selected variant's image.
    const selectedImageId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    if (!selectedImageId || !this.#showVariantMedia(selectedImageId)) {
      this.#showDefaultMedia();
    }
  }

  /**
   * [CUSTOM:hover-variant-metafield]
   * Update the card-gallery hover (secondary) image when the variant changes.
   * The variant-change section fetch strips the gallery media, so this is driven
   * client-side: each swatch input carries its variant's hover image URL in
   * `data-option-hover-image` (first item of theme.variant_images), and we point
   * the single secondary `<img>` at the selected swatch's URL. When the selected
   * variant has no hover image, the layer is hidden so hover falls back to the
   * primary image only.
   */
  #updateSecondaryImage() {
    const { cardGallery } = this.refs;
    if (!cardGallery) return;

    const layer = /** @type {HTMLElement | null} */ (cardGallery.querySelector('.card-gallery__media--secondary'));
    const image = layer?.querySelector('img');
    if (!(layer instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;

    const url = this.variantPicker?.selectedOption?.dataset.optionHoverImage;
    if (url) {
      image.srcset = this.#buildImageSrcset(url);
      image.src = url;
      layer.hidden = false;
    } else {
      layer.hidden = true;
    }
  }

  /**
   * [CUSTOM:hover-variant-metafield]
   * Builds a responsive srcset from a single Shopify CDN image URL by rewriting
   * its `width` query param across the theme's standard widths, so the swapped
   * hover image stays responsive against the existing `sizes` attribute.
   * @param {string} url - A Shopify CDN image URL containing a `width` param.
   * @returns {string} The srcset string.
   */
  #buildImageSrcset(url) {
    const widths = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
    return widths.map((w) => `${url.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
  }

  /**
   * [CUSTOM:swatch-variant-media]
   * Shows the card-gallery layer whose media id matches, hiding the others.
   * Returns false (without changing anything) if no layer matches the id — e.g.
   * combined-listing child media — so the caller can decide to fall back to default.
   * @param {string} id - The media id to show.
   * @returns {boolean} Whether a matching layer was found and shown.
   */
  #showVariantMedia(id) {
    const { cardGallery } = this.refs;
    if (!cardGallery || !id) return false;

    const layers = /** @type {NodeListOf<HTMLElement>} */ (cardGallery.querySelectorAll('[data-card-media-id]'));
    if (!layers.length) return false;

    const target = String(id);
    const hasMatch = Array.from(layers).some((layer) => layer.dataset.cardMediaId === target);
    if (!hasMatch) return false;

    for (const layer of layers) {
      layer.hidden = layer.dataset.cardMediaId !== target;
    }

    return true;
  }

  /**
   * [CUSTOM:swatch-variant-media]
   * Shows the default (primary) layer — used when no variant is selected. That layer
   * is the selected/first-available variant's media, or product.featured_media when it
   * has none; see [CUSTOM:variant-primary-media] in snippets/card-gallery.liquid.
   */
  #showDefaultMedia() {
    const { cardGallery } = this.refs;
    const fallback = /** @type {HTMLElement | null} */ (cardGallery?.querySelector('[data-card-media-default]'));
    if (fallback?.dataset.cardMediaId) this.#showVariantMedia(fallback.dataset.cardMediaId);
  }

  /**
   * [CUSTOM:swatch-variant-media]
   * Warms the hidden variant images by removing loading="lazy" so the browser
   * fetches them while still hidden (an eager display:none image is still loaded),
   * eliminating the first-hover flash. Runs once, on the first card hover.
   */
  #warmVariantMedia = () => {
    const { cardGallery } = this.refs;
    if (!cardGallery) return;

    const lazyImages = cardGallery.querySelectorAll('.card-gallery__media--variant img[loading="lazy"]');
    for (const image of lazyImages) image.removeAttribute('loading');
  };

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    // [CUSTOM:swatch-variant-media] preview the hovered swatch's image via layer toggle
    this.resetVariant.cancel();
    this.#showVariantMedia(id);
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    // [CUSTOM:swatch-variant-media] on pointerleave, fall back to the selected variant's
    // image, or the default featured layer when nothing is selected.
    const id = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    if (id) {
      this.#showVariantMedia(id);
      return;
    }

    this.#showDefaultMedia();
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // [CUSTOM:variant-url-sync]
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
   
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }
  
  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // [CUSTOM:swatch-legend-update]
    if (event.target instanceof HTMLInputElement) {
      const fieldset = event.target.closest('fieldset');
      const legendText = fieldset?.querySelector('.variant-option__legend-text');
      if (legendText) legendText.textContent = event.target.value;
    }

    // [CUSTOM:unavailable-swatch]
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }

  toggleSwatches(/** @type {Event} */ event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (!(overflowList instanceof OverflowList)) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (overflowList.hasAttribute('disabled')) {
          overflowList.removeAttribute('disabled');
        } else {
          overflowList.showAll();
        }
      });
    });
  }

}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
